import jwtDecode, { JwtPayload } from 'jwt-decode';

import { AuthFactor } from '../api/enumerations';
import { LoginProps } from '../api/login/types';

/** Which screen completes the second factor for this pending login. */
export type MfaMode = 'setup' | 'totp' | 'otp';

export interface MfaPendingSession {
  token: string;
  mode: MfaMode;
  /** From the login claims: an opaque token cannot be decoded for it. */
  exp?: number;
}

const mfaModes: Record<MfaMode, true> = { setup: true, totp: true, otp: true };

interface MfaClaims extends JwtPayload {
  /**
   * Set by the API on a pre-verification token. Absent on an older backend,
   * which is what keeps this front safe to deploy first (REAL-3264).
   */
  mfa_required?: boolean;
}

const decodeClaims = (token: string): MfaClaims | null => {
  try {
    return jwtDecode<MfaClaims>(token);
  } catch {
    return null;
  }
};

/** The answered claims win; a control center login only has its JWT. */
const isPendingToken = (response: LoginProps, accessToken: string): boolean =>
  (response.claims ?? decodeClaims(accessToken))?.mfa_required === true;

/**
 * A pending token lasts 30 min; past that every MFA call answers 401, so the
 * session it belongs to cannot be completed any more.
 */
const isSpentToken = ({ token, exp }: MfaPendingSession): boolean => {
  const now = Date.now() / 1000;
  if (exp !== undefined) return now > exp;

  const claims = decodeClaims(token);
  // No exp from the login and no JWT to read it from: no MFA call would accept it.
  if (!claims) return true;

  return claims.exp !== undefined && now > claims.exp;
};

const isMfaMode = (value: unknown): value is MfaMode =>
  typeof value === 'string' && value in mfaModes;

/**
 * Whether a stored entry still describes a login that can be finished: right
 * shape, and a token the API would still answer to.
 */
export const isUsableMfaPendingSession = (
  value: unknown
): value is MfaPendingSession => {
  if (typeof value !== 'object' || value === null) return false;

  const { token, mode, exp } = value as Partial<MfaPendingSession>;
  return (
    typeof token === 'string' &&
    token.length > 0 &&
    isMfaMode(mode) &&
    (exp === undefined || typeof exp === 'number') &&
    !isSpentToken({ token, mode, exp })
  );
};

/**
 * Which second factor screen this login needs, or null for a full session.
 *
 * The token claims are the source of truth for "is this pending" (answered
 * next to an opaque token, decoded from a JWT): the other response fields only
 * pick WHICH screen. An enforced user with no TOTP yet — including
 * one still on OTP, which does not satisfy the requirement — goes to the setup.
 */
export const resolveMfaMode = (response: LoginProps): MfaMode | null => {
  if (
    !response.access_token ||
    !isPendingToken(response, response.access_token)
  ) {
    return null;
  }
  if (response.mfa_setup_required) return 'setup';
  if (response.auth_factor === AuthFactor.TOTP) return 'totp';
  if (response.auth_factor === AuthFactor.OTP) return 'otp';

  // Pending without a configured factor: enrollment is the only way through.
  return 'setup';
};
